const CLARITY_DOMAIN = "claritygroup.com.sg";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INVALID_EMAIL_MESSAGE = "Enter a valid email address.";

export function normaliseClarityEmailInput(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) throw new Error("Email is required");

  const fullEmail = trimmed.includes("@")
    ? trimmed
    : `${trimmed}@${CLARITY_DOMAIN}`;

  const isValidEmail = EMAIL_REGEX.test(fullEmail);
  if (!isValidEmail) throw new Error(INVALID_EMAIL_MESSAGE);

  const [, domain = ""] = fullEmail.split("@");
  if (domain !== CLARITY_DOMAIN) {
    throw new Error(`Email must use @${CLARITY_DOMAIN}`);
  }

  return fullEmail;
}

export function normaliseExternalEmailInput(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) throw new Error("Email is required");
  if (!trimmed.includes("@")) throw new Error("Enter full email address");
  if (!EMAIL_REGEX.test(trimmed)) throw new Error(INVALID_EMAIL_MESSAGE);
  return trimmed;
}

/** Account activation / invite accept — any valid email domain (full address required). */
export function normalisePortalEmailInput(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) throw new Error("Email is required");
  if (!trimmed.includes("@")) throw new Error(INVALID_EMAIL_MESSAGE);
  if (!EMAIL_REGEX.test(trimmed)) throw new Error(INVALID_EMAIL_MESSAGE);
  return trimmed;
}

/** Source of truth for invite activation — normalises the email stored on the invite token. */
export function resolveInvitedActivationEmail(inviteEmail: string): string {
  return normalisePortalEmailInput(inviteEmail);
}

/** Reject client-submitted email that does not match the invited address (case/whitespace insensitive). */
export function assertInvitedActivationEmailMatch(
  invitedEmail: string,
  submittedEmail: string | null | undefined,
): void {
  const invited = resolveInvitedActivationEmail(invitedEmail);
  if (submittedEmail == null || !String(submittedEmail).trim()) return;
  const submitted = normalisePortalEmailInput(submittedEmail);
  if (submitted !== invited) {
    throw new Error("Email does not match this invitation.");
  }
}

export function formatSingaporePhoneE164(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) throw new Error("Phone number is required");

  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/[^\d+]/g, "");
  const plusSanitized = digits.startsWith("+")
    ? `+${digits.slice(1).replace(/\+/g, "")}`
    : digits.replace(/\+/g, "");

  if (hasPlus && /^\+\d{7,15}$/.test(plusSanitized) && !plusSanitized.startsWith("+65")) {
    return plusSanitized;
  }

  const numeric = trimmed.replace(/\D/g, "");
  if (/^\d{8}$/.test(numeric)) {
    return `+65${numeric}`;
  }
  if (/^65\d{8}$/.test(numeric)) {
    return `+${numeric}`;
  }
  if (/^\+65\d{8}$/.test(plusSanitized)) {
    return plusSanitized;
  }

  throw new Error("Enter a valid Singapore mobile number");
}
